import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { api, formatApiError, authQuery, API, downloadUrl } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useConfirm } from "../lib/confirm";
import { Upload, Trash2, Download, FileText, Scroll, Banknote, Megaphone, BookOpen, Folder } from "lucide-react";

const CATEGORIES = [
  { key: "Administrative", slug: "administrative", icon: FileText },
  { key: "Ritualistic", slug: "ritualistic", icon: Scroll },
  { key: "Financial", slug: "financial", icon: Banknote },
  { key: "Internal Notices", slug: "internal-notices", icon: Megaphone },
  { key: "Masonic Information", slug: "masonic-information", icon: BookOpen },
  { key: "Other", slug: "other", icon: Folder },
];

function prettySize(bytes) {
  if (!bytes) return "0 KB";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
}

export default function DocumentsPage() {
  const { t, i18n } = useTranslation();
  const locale =t("documents.en_gb");
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const confirm = useConfirm();
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState("");
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("Administrative");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/documents");
      setDocs(data);
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const m = {};
    for (const c of CATEGORIES) m[c.key] = [];
    for (const d of docs) {
      const c = d.category || "Administrative";
      if (!m[c]) m[c] = [];
      m[c].push(d);
    }
    return m;
  }, [docs]);

  const upload = async (e) => {
    e.preventDefault();
    if (!file) { toast.error(t("documents.selectFile")); return; }
    setUploading(true);
    setError("");
    const fd = new FormData();
    fd.append("file", file);
    fd.append("title", title || file.name);
    fd.append("description", "");
    fd.append("category", category);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setFile(null); setTitle("");
      const fileInput = document.getElementById("doc-file-input");
      if (fileInput) fileInput.value = "";
      toast.success(t("documents.uploadedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (d) => {
    const ok = await confirm({
      title: t("common.delete"),
      message: t("documents.deleteConfirm", { name: d.title || d.filename }),
    });
    if (!ok) return;
    try {
      await api.delete(`/documents/${d.id}`);
      toast.success(t("documents.deletedSuccess"));
      await load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="documents-page">
      <h1 className="page-title">{t("documents.title")}</h1>
      <div className="gold-divider my-8" />

      {error && <div className="error-banner mb-4">{error}</div>}

      {/* Upload box */}
      {isAdmin && (
        <div className="upload-box" data-testid="upload-box">
          <div className="upload-box-head">{t("documents.uploadTitle")}</div>
          <form onSubmit={upload} className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[200px]">
              <label className="label-lodge">{t("documents.documentName")}</label>
              <input className="input-lodge" value={title} onChange={(e) => setTitle(e.target.value)} placeholder={t("documents.documentName")} />
            </div>
            <div>
              <label className="label-lodge">{t("documents.category")}</label>
              <select className="input-lodge" value={category} onChange={(e) => setCategory(e.target.value)} data-testid="upload-category">
                {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{t(`documents.categories.${c.key}`)}</option>)}
              </select>
            </div>
            <div>
              <label className="label-lodge">{t("documents.file")}</label>
              {/* Hide the native file input — its OS-language "Choose
                  file / no file selected" label can't be translated by
                  the browser. We render a fully localized custom button
                  next to the actual selected filename. */}
              <input
                id="doc-file-input"
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                data-testid="upload-file-input"
                style={{ position: "absolute", left: -9999, width: 1, height: 1, opacity: 0 }}
              />
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="btn-outline-gold"
                  onClick={() => document.getElementById("doc-file-input")?.click()}
                  data-testid="upload-file-trigger"
                >
                  {t("documents.chooseFile", "Choose file")}
                </button>
                <span style={{ color: file ? "#f4e9c8" : "#9a7530", fontSize: "0.85rem", fontStyle: file ? "normal" : "italic" }}>
                  {file ? file.name : t("documents.noFileSelected", "No file selected")}
                </span>
              </div>
            </div>
            <button type="submit" className="btn-primary-gold flex items-center gap-2" disabled={uploading} data-testid="upload-doc-btn">
              <Upload size={14} /> {uploading ? "..." : t("documents.upload")}
            </button>
            <div className="w-full text-xs" style={{ color: "#858481", fontStyle: "italic", fontSize: "0.78rem" }}>
              {t("documents.accepted")}
            </div>
          </form>
        </div>
      )}

      {CATEGORIES.map(({ key, slug, icon: Icon }) => {
        const items = grouped[key] || [];
        return (
          <div key={key} className="doc-category" data-testid={`doc-cat-${slug}`}>
            <div className={`doc-category-head ${slug}`}>
              <Icon size={16} strokeWidth={1.6} />
              <span>{t(`documents.categories.${key}`).toUpperCase()}</span>
              <span className="count">{items.length}</span>
            </div>
            {items.length === 0 ? (
              <div style={{ background: "#fff", border: "1px solid var(--cream-200)", borderTop: "none", padding: "1rem 1.2rem", fontStyle: "italic", color: "#858481", fontSize: "0.9rem" }}>
                {t("documents.noneInCategory")}
              </div>
            ) : (
              <div
                className="doc-category-scroll"
                style={{
                  maxHeight: items.length > 5 ? "calc(5 * 54px + 42px)" : undefined,
                  overflowY: items.length > 5 ? "auto" : "visible",
                  background: "#fff",
                  border: "1px solid var(--cream-200)",
                  borderTop: "none",
                }}
                data-testid={`doc-cat-scroll-${slug}`}
              >
                <table className="doc-category-table" style={{ marginTop: 0 }}>
                <thead>
                  <tr>
                    <th>{t("documents.documentName")}</th>
                    <th>{t("common.size")}</th>
                    <th>{t("common.date")}</th>
                    <th style={{ textAlign: "right" }}>{t("common.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((d) => (
                    <tr key={d.id}>
                      <td>
                        <div className="flex items-center gap-2">
                          <FileText size={14} color="#9a7530" />
                          <span style={{ fontFamily: "Crimson Text, serif" }}>{d.title || d.filename}</span>
                        </div>
                      </td>
                      <td>{prettySize(d.size)}</td>
                      <td>{new Date(d.created_at).toLocaleDateString(locale, { year: "numeric", month: "2-digit", day: "2-digit" })}</td>
                      <td style={{ textAlign: "right" }}>
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            className="btn-outline-gold flex items-center gap-1"
                            onClick={() => downloadUrl(`${API}/documents/${d.id}/download${authQuery()}`, d.title || d.filename)}
                            data-testid={`download-doc-${d.id}`}
                            style={{ textDecoration: "none", background: "transparent", border: "1px solid #c9a84c", cursor: "pointer" }}
                          >
                            <Download size={12} /> {t("common.download")}
                          </button>
                          {isAdmin && (
                            <button className="btn-danger flex items-center gap-1" onClick={() => remove(d)} data-testid={`del-doc-${d.id}`}>
                              <Trash2 size={12} /> {t("common.delete")}
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
